import React, { useMemo } from 'react';
import { ensuredForwardRef } from 'react-use';
import ReactDatePicker from 'react-datepicker';
import { pick, omit } from 'ramda';
import { DatePickerWrapperStyles } from './styled';
import { DatePickerInput, Container, PopperContainer, Header, WeekDay } from './components';
import { sameWidth } from './same-width-modifier';
import 'react-datepicker/dist/react-datepicker.css';
const textInputProps = [
    'fixedPadding',
    'hint',
    'hintType',
    'textType',
    'label',
    'labelTooltip',
    'appendInnerIcon',
    'prependInnerIcon',
    'sizeComponent',
    'permanentHint',
    'onClear',
    'mask'
];
const DEFAULT_DATE_FORMAT = 'MM/dd/yyyy';
/**
 * @typedef {Object} IDatePickerProps
 * @extends {IHintExtentionProps}
 * @extends {IFormLabelExtentionProps}
 * @property {ReactDatePickerProps['popperPlacement']} [pickerPlacement] - The placement of the date picker popper.
 * @property {ReactDatePickerProps['popperModifiers']} [pickerModifiers] - Modifiers for the date picker popper.
 * @property {ReactDatePickerProps['placeholderText']} [placeholder] - Placeholder text for the date picker input.
 * @property {boolean} [pickerWidthSameAsInput] - If true, the popper width will be the same as the input width.
 * @property {string} [dateFormat=DEFAULT_DATE_FORMAT] - The date format for the date picker.
 */
/**
 * Date picker component with the possibility to select day, time, or a range of them.
 * @component
 * @param {IDatePickerProps} props - Props for the DatePicker component.
 * @param {React.Ref} calendarRef - Ref for the calendar component.
 * @returns {JSX.Element} The DatePicker component.
 */
/**
 * @description WIP(!) Next versions could having incompatibilities
 *  Datepicker component with possibility to select, day, time, range of them.
 */
export const DatePicker = ensuredForwardRef(({ pickerPlacement = 'bottom-start', pickerModifiers = [], dateFormat = DEFAULT_DATE_FORMAT, showIcon = true, showMonthDropdown, showYearDropdown, calendarContainer = Container, popperContainer = PopperContainer, formatWeekDay = WeekDay, customInput: CustomInput = DatePickerInput, renderCustomHeader, placeholder, pickerWidthSameAsInput, isClearable, ...props }, calendarRef) => {
    const inputProps = pick(textInputProps, props);
    const datePickerProps = omit(textInputProps, props);
    const maskProps = useMemo(() => {
        const mask = [];
        try {
            dateFormat.split(/(\W)/).forEach((unit) => {
                switch (unit) {
                    case 'dd':
                        mask.push(/[0-3]/, /\d/);
                        break;
                    case 'MM':
                        mask.push(/[0-1]/, /\d/);
                        break;
                    case 'yyyy':
                        mask.push(/\d/, /\d/, /\d/, /\d/);
                        break;
                    case 'yy':
                        mask.push(/\d/, /\d/);
                        break;
                    case 'hh':
                        mask.push(/[0-1]/, /\d/);
                        break;
                    case 'HH':
                        mask.push(/[0-2]/, /\d/);
                        break;
                    case 'mm':
                    case 'ss':
                        mask.push(/[0-5]/, /\d/);
                        break;
                    case 'a':
                    case 'aa':
                        mask.push(/A|P/, 'M');
                        break;
                    case 'aaa':
                        mask.push(/a|p/, 'm');
                        break;
                    case 'aaaa':
                        mask.push(/a|p/, '.', 'm', '.');
                        break;
                    case 'aaaaa':
                        mask.push(/a|p/);
                        break;
                    case 'QQQ':
                        mask.push('Q', /[1-4]/);
                        break;
                    case ' ':
                        break;
                    default:
                        if (/\W/g.test(unit)) {
                            mask.push(...unit.split('').join());
                            break;
                        }
                        else {
                            const errMsg = `unknown unit time format: ${unit}`;
                            console.warn(errMsg);
                            /* if we can't parse dateFormat
                               we will throw Error and won't use mask
                            */
                            throw new Error(errMsg);
                        }
                }
            });
            return {
                mask,
                guide: true,
                keepCharPositions: true
            };
        }
        catch (error) {
            return {};
        }
    }, [dateFormat]);
    // @ts-ignore
    const periodType = useMemo(() => {
        var _a;
        const options = {
            year: props.showYearPicker,
            quarter: props.showQuarterYearPicker,
            month: props.showMonthYearPicker
        };
        return ((_a = Object.entries(options).find(([key, value]) => value)) === null || _a === void 0 ? void 0 : _a[0]) || 'day';
    }, [props.showYearPicker, props.showQuarterYearPicker, props.showMonthYearPicker]);
    const customHeaderRenderer = (renderProps) => {
        if (renderCustomHeader)
            return renderCustomHeader(renderProps);
        return (React.createElement(Header, { ...props, showMonthDropdown: showMonthDropdown, showYearDropdown: showYearDropdown, calendarRef: calendarRef, periodType: periodType, ...renderProps }));
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(ReactDatePicker, { ref: calendarRef, clearButtonClassName: "date-picker clear-button", wrapperClassName: "date-picker full-width", dateFormat: dateFormat, customInput: 
            /* @ts-ignore */
            React.createElement(CustomInput, { isClearable: isClearable, showIcon: showIcon, ...inputProps, ...maskProps }), calendarContainer: calendarContainer, popperContainer: popperContainer, popperPlacement: pickerPlacement, popperModifiers: [(pickerWidthSameAsInput ? sameWidth : {}), ...pickerModifiers], renderCustomHeader: customHeaderRenderer, formatWeekDay: formatWeekDay, placeholderText: placeholder, ...datePickerProps, showPopperArrow: false }),
        React.createElement(DatePickerWrapperStyles, null)));
});
DatePicker.displayName = 'DatePicker';
